import React, { useState, useEffect } from 'react'
import * as ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import * as wjCore from '@mescius/wijmo'
import * as wjcGrid from '@mescius/wijmo.grid'
import { FlexGridFilter } from '@mescius/wijmo.grid.filter'
import * as wjMultiRow from '@mescius/wijmo.react.grid.multirow'
import '@mescius/wijmo.cultures/wijmo.culture.ja'
import customContextMenu from '../../wijmo/customContextMenu'
import colElements from '../../wijmo/columnElements'
import { idPresentation } from '../../utility'
import renderWorkflowStateBadge from '../element/WorkflowStateBadge'
import renderPeriodBadge from '../element/PeriodBadge'
import ErrorBoundary from '../ErrorBoundary'
import moment from 'moment'

function getLayoutDefs (mode, characters) {
  const isShopro = (mode === 'shoproView')
  const linkButtonCol = {
    cssClassAll: 'wj-style-basic',
    width: 80,
    isReadOnly: true,
    align: 'center',
    cellTemplate: (ctx) => {
      if (ctx.item.parent.workflow?.is_approved && moment(ctx.item.parent.period_to).add(1, 'd').isAfter(moment())) {
        const url = ctx.item.parent.use_uri || '#'
        return ctx.item.canceled_at ? '<span class="text-danger">使用不可</span>' : `<a href="${url}" class="btn bg-theme-success py-0">使用</a>`
      } else {
        const url = ctx.item.parent.application_uri || '#'
        return `<a href="${url}" class="btn ol-theme-success py-0">詳細</a>`
      }
    }
  }
  const ldTwoLines = [
    {
      colspan: 3,
      cells: [
        { ...colElements.xr.id, width: 125, rowspan: 2 },
        { ...colElements.xr.character, width: 200, dataMap: new wjcGrid.DataMap(characters, 'id', 'title'), rowspan: 2, wordWrap: true },
        { ...colElements.xr.comment, width: 200, rowspan: 2, wordWrap: true }
      ]
    },
    {
      colspan: 2,
      cells: [
        { ...colElements.xr.royality, aggregate: 'Sum' },
        { ...colElements.xr.spent_royality, aggregate: 'Sum', rowspan: 2 },
        colElements.xr.rate_decimal
      ]
    },
    isShopro
      ? null
      : {
          colspan: 1,
          cells: [
            { ...linkButtonCol, rowspan: 2 }
          ]
        }
  ].filter((col) => col)
  return {
    def: ldTwoLines,
    frozenColumns: 3
  }
}

//
export default function XrContractsGrid (props) {
  const [contractsCV, setContractsCV] = useState(null)
  const [layoutDef] = useState(getLayoutDefs(props.mode, props.characters))
  const [jsonElem] = useState(props.itemsJsonId ? $('#' + props.itemsJsonId) : null)

  // インポート/エクスポート時のテーブルを定義
  const tableFormat = [
    { field: 'xr_contract_id', text: '申請ID', type: 'string', isEditable: false },
    { field: 'coop_con_id', text: '契約ID', type: 'string', isEditable: false },
    { field: 'character_title', text: '版権名', type: 'string', isEditable: false },
    { field: 'comment', text: 'キャラクターメモ', type: 'string', isEditable: false },
    { field: 'royality', text: '使用料', type: 'number', isEditable: false },
    { field: 'rate_decimal', text: '料率', type: 'number', isEditable: false },
    { field: 'spent_royality', text: '使用額', type: 'number', isEditable: false }
  ]

  useEffect(() => {
    if (!contractsCV) {
      props.contracts.forEach((contract) => {
        if (!contract.workflow) {
          contract.workflow = { is_approved: false, is_returned: false }
        }
        contract.toString = () => {
          const contractStr = `<b>申請ID【${idPresentation(contract.id)}】</b>`
          if (props.mode !== 'shoproView') {
            const wfStatusBadge = `<span class="workflow-state-badge" data-workflow='${JSON.stringify(contract.workflow, ['is_approved', 'is_returned'])}'></span>`
            const periodBadge = `<span class="period-badge" data-period-from="${contract.period_from}" data-period-to="${contract.period_to}"></span>`
            return `<span>${wfStatusBadge}${periodBadge}<br/><a href="${contract.application_uri}">${contractStr}</a></span>`
          } else {
            const periodBadge = `<span class="period-badge" data-period-from="${contract.period_from}" data-period-to="${contract.period_to}"></span>`
            return `<span>${periodBadge}${contractStr}<br/></span>`
          }
        }
        // wijmoのグループ化機能を使い、親のcontractに基づいてグループ化するための処理
        contract.contracts.forEach((child) => {
          child.parent = contract
        })
      })
      const characterMap = new Map(props.characters.map((character) => [character.id, character.title]))
      const contracts = props.contracts.map((contract) => contract.contracts).flat()
      const inputFields = 'id,royality'.split(',')
      contracts.map((contract, i) => {
        contract.rate = Number(contract.rate)
        contract.character_title = characterMap.has(contract.character_id) ? characterMap.get(contract.character_id) : contract.character_name_free
        return 0
      })
      const _contractsCV = new wjCore.CollectionView(contracts, {
        calculatedFields: {
          rate_decimal: (obj) => obj.rate / 100
        },
        groupDescriptions: ['parent']
      })
      _contractsCV.collectionChanged.addHandler((cv, e) => {
        // アイテム情報のJSONを隠し要素に記録（フォーム送信時に参照する）
        jsonElem && jsonElem.val(JSON.stringify(cv.items, inputFields))
      })
      jsonElem && jsonElem.val(JSON.stringify(_contractsCV.items, inputFields))

      setContractsCV(_contractsCV)
    }
  }, [contractsCV])

  const gridInit = (grid) => {
    grid.tableFormat = tableFormat
    customContextMenu(grid)

    const filter = new FlexGridFilter(grid)
    filter.filterColumns = 'id,xr_contract_id,character_id,period_from,period_to,royality,rate_decimal'.split(',')
    grid.formatItem.addHandler((s, e) => {
      if (e.panel === s.cells) {
        if (e.cell.classList.contains('wj-group')) {
          renderWorkflowStateBadge(e.cell)
          renderPeriodBadge(e.cell)
        }
      }
    })
  }

  return (
    <div className="d-inline-block w-100 text-center">
      <wjMultiRow.MultiRow showMarquee={true} stickyHeaders={true} itemsSource={contractsCV} layoutDefinition={layoutDef.def}
        frozenColumns={layoutDef.frozenColumns} multiRowGroupHeaders={true} initialized={gridInit} keyActionTab={wjcGrid.KeyAction.Cycle}
        groupHeaderFormat="{value}" style={{ maxHeight: '600px' }}>
      </wjMultiRow.MultiRow>
    </div>
  )
}

XrContractsGrid.propTypes = {
  contracts: PropTypes.arrayOf(PropTypes.object),
  characters: PropTypes.arrayOf(PropTypes.object),
  mode: PropTypes.string,
  itemsJsonId: PropTypes.string,
  oldItemsJson: PropTypes.arrayOf(PropTypes.object)
}

XrContractsGrid.defaultProps = {
  itemsJsonId: '',
  oldItemsJson: []
}

$('#xr-contracts-grid').each((i, el) => {
  const qel = $(el)
  const props = qel.data() // bladeでdata-xxxx形式で指定した値がpropsとして参照できる
  ReactDOM.render(
    <ErrorBoundary>
      <XrContractsGrid {...props}/>
    </ErrorBoundary>,
    el
  )

  Object.keys(props).map((key) => { // ソースにdata-属性が残らないように削除
    qel.removeAttr('data-' + key.replace(/[A-Z]/g, (s) => '-' + s.charAt(0).toLowerCase()))
    return 0
  })
})
